import { useEffect } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useLocation } from 'react-router';
import { useI18n, useSelector } from '~Hooks';
import {
  createPayment,
  createPaymentVariables,
  getPayment,
  getPaymentVariables,
} from './__queries__';
import { PrintRow } from '~Components/PrinterManager';
import { MethodProps } from '../types';

const PAYMENT_QUERY = gql`
  query getPayment($id: ID!) {
    payment: allPayments(filter: { id: $id }) {
      id
      status
    }
  }
`;

const CREATE_PAYMENT = gql`
  mutation createPayment(
    $sessionId: ID
    $venueId: ID!
    $staffId: String
    $amount: Int!
    $discountRate: Int
  ) {
    createPosPayment(
      amount: $amount
      discountRate: $discountRate
      currency: "SEK"
      discountType: "PERCENTAGE"
      venueId: $venueId
      staffId: $staffId
      tableSessionId: $sessionId
    ) {
      success
      payment
      message
    }
  }
`;

const ScanPayment: React.FunctionComponent<MethodProps> = ({
  payment,
  amount,
  discount,
  onMetaChange,
  onComplete,
  onFail,
}) => {
  const staffId = useSelector((state) => state.staff?.id);
  const location = useLocation();
  const { paymentId } = payment.meta;
  const [, , venueId] = location.pathname.split('/');
  const { i18n } = useI18n();

  const client = useApolloClient();
  const [createPayment] = useMutation<createPayment, createPaymentVariables>(CREATE_PAYMENT);

  useEffect(() => {
    createPayment({
      variables: {
        amount: amount!,
        discountRate: discount,
        venueId,
        staffId,
      },
    }).then(({ data }) => {
      if (data?.createPosPayment.success) {
        const message: PrintRow[] = [
          { content: `${i18n('Scan.ToPay')}: ${amount! / 100}` },
          {
            content: `${i18n('Scan.Pay')}:${data.createPosPayment.payment.id}:${amount}`,
            qr: true,
          },
        ];

        window.printerManager?.initiatePrint({
          data: message,
          printType: 'PAYMENT_SCAN',
        });

        onMetaChange({
          paymentId: data.createPosPayment.payment.id,
          amount,
        });
      } else {
        onFail(data?.createPosPayment.message);
      }
    });
  }, []);

  useEffect(() => {
    if (!paymentId) {
      return () => {
        // Do nothing
      };
    }

    const query = client.watchQuery<getPayment, getPaymentVariables>({
      query: PAYMENT_QUERY,
      fetchPolicy: 'no-cache',
      variables: {
        id: paymentId,
      },
    });

    const interval = setInterval(() => {
      query.refetch().then((res) => {
        if (res.data.payment && res.data.payment[0] && res.data.payment[0].status !== 'PENDING') {
          onComplete(paymentId);
          clearInterval(interval);
        }
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [paymentId]);

  return null;
};

export default ScanPayment;
